// @ts-strict-ignore
import * as React from 'react';
import Paragraph from 'antd/lib/typography/Paragraph';
import Text from 'antd/lib/typography/Text';
import { formatDateWithTimezone } from '../timeHelpers';
import { runwayColors } from './styles/globalStyles';
import { AppStoreReviewData, RecentReview, SubscriptionFeatureType } from '../types';
import Tabs from 'antd/lib/tabs';
import sparkleIcon from '../../assets/sparkle.svg';
import Space from 'antd/lib/space';
import List from 'antd/lib/list';
import Tooltip from 'antd/lib/tooltip';
import { PlanRestrictedFeatureWrapper } from './planRestrictions/PlanRestrictedFeatureWrapper';
import { useRestrictedFeatureIsDisabled } from './planRestrictions/planRestrictionHelperComponents';
import { useSelector } from 'react-redux';
import {
    selectAIDisabledForAppStoreReviews,
    selectAppTimeFormatSetting,
    selectEffectiveTimezone,
} from '@/selectors/selectors';
import { AppComponentProps } from '@/typescriptHelpers';
import { RootState } from '@/reducers/reducers';
import { Destination } from '@/types/destinations';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';

interface RatingsReviewsProps extends AppComponentProps {
    reviewData: Pick<AppStoreReviewData, 'recentReviews' | 'issues'>;
    destinationsByStepConfigId?: Record<string, Destination>;
}

const RatingsReviews: React.FC<RatingsReviewsProps> = (props: RatingsReviewsProps) => {
    const { reviewData, destinationsByStepConfigId } = props;

    const isReviewIssuesFeatureDisabled = useRestrictedFeatureIsDisabled(SubscriptionFeatureType.ReviewIssues);
    const isReviewIssuesFeatureDisabledForOrg = useSelector((state: RootState) =>
        selectAIDisabledForAppStoreReviews(state, props.orgId),
    );
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));

    const reviews = reviewData?.recentReviews ?? [];
    const reviewIssues = reviewData?.issues ?? [];

    const showReviewIssues = !isReviewIssuesFeatureDisabledForOrg;

    return (
        <Tabs
            items={[
                {
                    key: 'recent',
                    label: 'Recent reviews',
                    children: (
                        <RecentReviewsModule
                            reviews={reviews}
                            effectiveTimezone={effectiveTimezone}
                            use24HourTimeFormat={use24HourTimeFormat}
                            destinationsByStepConfigId={destinationsByStepConfigId}
                        />
                    ),
                },
                showReviewIssues && {
                    key: 'issues',
                    label: (
                        <Space>
                            <PlanRestrictedFeatureWrapper
                                feature={SubscriptionFeatureType.ReviewIssues}
                                requiresTooltip
                            />
                            <Tooltip title="These issues were detected using AI">
                                <img src={sparkleIcon} width={15} height={15} />
                            </Tooltip>
                            Issues
                        </Space>
                    ),
                    disabled: isReviewIssuesFeatureDisabled,
                    children: (
                        <List
                            rowKey={(issue) => issue.description}
                            renderItem={(issue) => {
                                return (
                                    <List.Item
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: 12,
                                        }}
                                    >
                                        <Space align="center">
                                            <img src={sparkleIcon} width={15} height={15} />
                                            <Text strong style={{ fontSize: 15 }}>
                                                {issue.description}
                                            </Text>
                                        </Space>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Text>Examples:</Text>
                                            <RecentReviewsModule
                                                reviews={issue.examples}
                                                effectiveTimezone={effectiveTimezone}
                                                use24HourTimeFormat={use24HourTimeFormat}
                                            />
                                        </div>
                                    </List.Item>
                                );
                            }}
                            dataSource={reviewIssues}
                            locale={{ emptyText: 'No issues detected' }}
                        />
                    ),
                },
            ]}
        ></Tabs>
    );
};

export default RatingsReviews;

const RecentReviewsModule = ({
    reviews,
    effectiveTimezone,
    use24HourTimeFormat,
    destinationsByStepConfigId,
}: {
    reviews: RecentReview[];
    effectiveTimezone: string;
    use24HourTimeFormat: boolean;
    destinationsByStepConfigId?: Record<string, Destination>;
}) => {
    return (
        <List
            renderItem={(review) => {
                const primaryContent = review.contentTranslated ?? review.content;
                const secondaryContent = review.contentTranslated ? review.content : null;

                let bottomLine = formatDateWithTimezone(
                    review.submittedAt,
                    'dateOnly',
                    effectiveTimezone,
                    use24HourTimeFormat,
                );
                if (review.author != '') {
                    bottomLine += ' · ' + review.author;
                }
                if (review.androidUserOSVersion) {
                    bottomLine += ' · ' + review.androidUserOSVersion;
                }
                if (review.userDeviceData?.productName) {
                    bottomLine += ' · ' + review.userDeviceData.productName;
                }
                const destination = destinationsByStepConfigId?.[review.destinationStepConfigId];
                return (
                    <List.Item>
                        <div>
                            {destination && (
                                <span style={{ marginRight: 6, verticalAlign: 'middle' }}>
                                    <DestinationCounter destinations={[destination]} size={16} />
                                </span>
                            )}
                            <Text strong style={{ color: runwayColors.statusYellow }}>
                                {'★'.repeat(parseInt(review.rating))}
                            </Text>

                            <Text strong> · {review.titleTranslated ?? review.title}</Text>
                            <Paragraph
                                ellipsis={{ rows: 2, expandable: true }}
                                style={{ marginTop: 2, marginBottom: 0 }}
                            >
                                {primaryContent}
                            </Paragraph>
                            {secondaryContent && (
                                <Paragraph
                                    ellipsis={{ rows: 2, expandable: true }}
                                    style={{ marginTop: 2, marginBottom: 0, fontStyle: 'italic' }}
                                >
                                    {secondaryContent}
                                </Paragraph>
                            )}
                            <Text type="secondary" style={{ marginTop: 2 }}>
                                {bottomLine}
                            </Text>
                        </div>
                    </List.Item>
                );
            }}
            dataSource={reviews}
            locale={{ emptyText: 'No recent reviews' }}
            split={false}
        />
    );
};
